// ============================================================
// src/firestore.js — Operaciones con Firestore
// FASE 4: logging y manejo centralizado de errores
// ============================================================

import {
  collection, addDoc, updateDoc, deleteDoc,
  doc, query, where, getDocs,
  onSnapshot, serverTimestamp, Timestamp,
  increment, writeBatch, runTransaction
} from 'firebase/firestore';

import { db } from './firebase.js';

import { logInfo } from './core/logger.js';
import { manejarError } from './core/errors.js';

// ============================================================
// Colección principal
// ============================================================

const colEdiciones = collection(db, 'editions');
let cancelarEscucha = null;
let _callbackCambioEdiciones = null;

export function registrarCallbackCambioEdiciones(fn) {
  _callbackCambioEdiciones = fn;
}

// ============================================================
// Escucha en tiempo real
// ============================================================

export function iniciarEscuchaEdiciones(idUsuario) {
  detenerEscuchaEdiciones();

  const consulta = query(
    colEdiciones,
    where('userId', '==', idUsuario)
  );

  cancelarEscucha = onSnapshot(
    consulta,
    (snapshot) => {
      const ediciones = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
        fechaInicio: docSnap.data().fechaInicio?.toDate?.() ?? null,
        fechaFin: docSnap.data().fechaFin?.toDate?.() ?? null,
      }));

      logInfo('Firestore', `${ediciones.length} ediciones cargadas`);
      if (_callbackCambioEdiciones) _callbackCambioEdiciones(ediciones);
    },
    (error) => {
      manejarError(error, 'Firestore escuchar ediciones');
    }
  );
}

export function detenerEscuchaEdiciones() {
  if (cancelarEscucha) {
    cancelarEscucha();
    cancelarEscucha = null;
    logInfo('Firestore', 'Escucha detenida');
  }
}

// ============================================================
// Crear edición
// ============================================================

export async function agregarEdicion(idUsuario, datosEdicion) {
  try {
    const docRef = await addDoc(colEdiciones, {
      userId: idUsuario,
      concurso: datosEdicion.concurso ?? '',
      año: datosEdicion.año ?? new Date().getFullYear(),
      modo: datosEdicion.modo ?? '',
      fechaInicio: datosEdicion.fechaInicio
        ? Timestamp.fromDate(new Date(datosEdicion.fechaInicio))
        : null,
      fechaFin: datosEdicion.fechaFin
        ? Timestamp.fromDate(new Date(datosEdicion.fechaFin))
        : null,
      webUrl: datosEdicion.webUrl ?? '',
      rawScore: datosEdicion.rawScore ?? 0,
      finalScore: datosEdicion.finalScore ?? 0,
      posicion: datosEdicion.posicion ?? null,
      cantidadCertificados: 0,
      creadoEn: serverTimestamp(),
    });

    logInfo('Firestore', `Edición creada ${docRef.id}`);
    return docRef.id;
  } catch (error) {
    manejarError(error, 'Firestore agregarEdicion');
    return null;
  }
}

// ============================================================
// Actualizar edición
// ============================================================

export async function actualizarEdicion(idEdicion, datosActualizados) {
  try {
    const refDoc = doc(db, 'editions', idEdicion);

    await updateDoc(refDoc, {
      concurso: datosActualizados.concurso ?? '',
      año: datosActualizados.año ?? new Date().getFullYear(),
      modo: datosActualizados.modo ?? '',
      fechaInicio: datosActualizados.fechaInicio
        ? Timestamp.fromDate(new Date(datosActualizados.fechaInicio))
        : null,
      fechaFin: datosActualizados.fechaFin
        ? Timestamp.fromDate(new Date(datosActualizados.fechaFin))
        : null,
      webUrl: datosActualizados.webUrl ?? '',
      rawScore: datosActualizados.rawScore ?? 0,
      finalScore: datosActualizados.finalScore ?? 0,
      posicion: datosActualizados.posicion ?? null,
      actualizadoEn: serverTimestamp(),
    });

    logInfo('Firestore', `Edición actualizada ${idEdicion}`);
    return true;
  } catch (error) {
    manejarError(error, 'Firestore actualizarEdicion');
    return false;
  }
}

// ============================================================
// Guardar folderIds de Drive
// ============================================================

export async function guardarFolderIds(idEdicion, folderIds) {
  try {
    const refDoc = doc(db, 'editions', idEdicion);

    await updateDoc(refDoc, {
      folderIds,
      actualizadoEn: serverTimestamp(),
    });

    logInfo('Firestore', `folderIds guardados en ${idEdicion}`);
    return true;
  } catch (error) {
    manejarError(error, 'Firestore guardarFolderIds', { mostrar: false });
    return false;
  }
}

// ============================================================
// Eliminar edición
// ============================================================

export async function eliminarEdicion(idEdicion) {
  try {
    const refDoc = doc(db, 'editions', idEdicion);
    await deleteDoc(refDoc);

    logInfo('Firestore', `Edición eliminada ${idEdicion}`);
    return true;
  } catch (error) {
    manejarError(error, 'Firestore eliminarEdicion');
    return false;
  }
}

// ============================================================
// Archivos de una edición
// ============================================================

export async function eliminarArchivoDeEdicion(idEdicion, idArchivo) {
  try {
    const refArchivo = doc(db, 'editions', idEdicion, 'files', idArchivo);
    const refEdicion = doc(db, 'editions', idEdicion);

    await runTransaction(db, async (tx) => {
      const snapArchivo = await tx.get(refArchivo);
      const snapEdicion = await tx.get(refEdicion);
      const esCertificado = snapArchivo.data()?.tipo === 'cert';

      tx.delete(refArchivo);

      if (esCertificado) {
        const actual = Number(snapEdicion.data()?.cantidadCertificados || 0);
        tx.update(refEdicion, { cantidadCertificados: Math.max(0, actual - 1) });
      }
    });

    logInfo('Firestore', `Archivo eliminado ${idArchivo}`);
    return true;
  } catch (error) {
    manejarError(error, 'Firestore eliminarArchivo', { mostrar: false });
    return false;
  }
}

export async function guardarArchivoEnEdicion(idEdicion, datosArchivo) {
  try {
    const colFiles = collection(db, 'editions', idEdicion, 'files');
    const refArchivo = doc(colFiles);
    const refEdicion = doc(db, 'editions', idEdicion);
    const tipo = datosArchivo.tipo ?? '';

    const batch = writeBatch(db);

    batch.set(refArchivo, {
      storageType:  datosArchivo.storageType  ?? 'user_drive',
      fileId:       datosArchivo.fileId       ?? '',
      fileUrl:      datosArchivo.fileUrl      ?? null,
      providerMeta: datosArchivo.providerMeta ?? null,
      tipo,
      nombre:       datosArchivo.nombre       ?? '',
      mimeType:     datosArchivo.mimeType     ?? '',
      tamaño:       datosArchivo.tamaño       ?? 0,
      subidoEn:     serverTimestamp(),
    });

    if (tipo === 'cert') {
      batch.update(refEdicion, {
        cantidadCertificados: increment(1),
      });
    }

    await batch.commit();

    logInfo('Firestore', `Archivo guardado ${refArchivo.id}`);
    return refArchivo.id;
  } catch (error) {
    manejarError(error, 'Firestore guardarArchivo');
    return null;
  }
}

export async function obtenerArchivosEdicion(idEdicion, tipo) {
  try {
    const colFiles = collection(db, 'editions', idEdicion, 'files');
    const consulta = query(colFiles, where('tipo', '==', tipo));
    const snapshot = await getDocs(consulta);

    return snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
      subidoEn: docSnap.data().subidoEn?.toDate?.() ?? null,
    }));
  } catch (error) {
    manejarError(error, 'Firestore obtenerArchivos', { mostrar: false });
    return [];
  }
}

export async function actualizarShackVisibleArchivo(idEdicion, idArchivo, shackVisible) {
  try {
    const refDoc = doc(db, 'editions', idEdicion, 'files', idArchivo);
    await updateDoc(refDoc, { shackVisible: Boolean(shackVisible) });
    return true;
  } catch (error) {
    manejarError(error, 'Firestore actualizarShackVisible', { mostrar: false });
    return false;
  }
}

export async function contarArchivosPorTipo(idEdicion) {
  try {
    const colFiles = collection(db, 'editions', idEdicion, 'files');
    const snapshot = await getDocs(colFiles);

    const conteos = { log: 0, conf: 0, res: 0, cert: 0, reg: 0 };

    snapshot.docs.forEach((docSnap) => {
      const tipo = docSnap.data().tipo;
      if (tipo in conteos) conteos[tipo]++;
    });

    return conteos;
  } catch (error) {
    manejarError(error, 'Firestore contarArchivos', { mostrar: false });
    return { log: 0, conf: 0, res: 0, cert: 0, reg: 0 };
  }
}

